import { Check, FileText, Pencil, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useToast } from "../../hooks/use-toast";
import api from "../../lib/api";
import { B2C_MASTER_PRODUCTS_COLUMNS, UPM_MASTER_PRODUCTS_COLUMNS } from "../../lib/constants";
import { Loader } from "../layout/Loader";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import Pagination from "../ui/pagination";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../ui/table";

export default function B2CProductsMaster() {
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const [total, setTotal] = useState(0);
	const [b2cProducts, setB2cProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [editProductData, setEditProductData] = useState<any>({});
	const { toast } = useToast();
	const [descModalOpen, setDescModalOpen] = useState(false);
	const [descEdit, setDescEdit] = useState<string>("");
	const [descProduct, setDescProduct] = useState<any>(null);
	const [activeView, setActiveView] = useState<"rx" | "upm">("rx");

	const abortRef = useRef<AbortController | null>(null);

	const getB2CProducts = async () => {
		setLoading(true);
		try {
			// Cancel any in-flight request before starting a new one
			if (abortRef.current) {
				abortRef.current.abort();
			}
			abortRef.current = new AbortController();

			if (activeView === "rx") {
				const res: any = await api.getB2CMasterProducts(search, page, limit, {
					signal: abortRef.current.signal,
					timeoutMs: 10000,
				});
				if (!res || res.success === false || !Array.isArray(res?.data)) {
					setB2cProducts([]);
					setTotal(0);
					if (res?.message) {
						toast({ title: "Failed to load products", description: res.message, variant: "destructive" });
					}
					return;
				}

				const processedData = res.data.map((product: any) => {
					let currentPrice = product?.mrp ?? null;
					try {
						const details = product?.upm_product_details;
						if (details && typeof details === "object") {
							currentPrice =
								details.current_price || details.price || details.selling_price || product.mrp;
						}
					} catch (_e) {
						currentPrice = product?.mrp ?? null;
					}

					return { ...product, current_price: currentPrice };
				});

				setB2cProducts(processedData);
				setTotal(Number(res?.totalCount ?? 0));
				return;
			}

			const res: any = await api.getUPMMasterProducts(search, page, limit, {
				signal: abortRef.current.signal,
				timeoutMs: 10000,
			});
			if (!res || res.success === false || !Array.isArray(res?.data)) {
				setB2cProducts([]);
				setTotal(0);
				if (res?.message) {
					toast({ title: "Failed to load products", description: res.message, variant: "destructive" });
				}
				return;
			}

			const processedData = res.data.map((product: any) => ({
				...product,
				current_price: product?.current_price ?? product?.mrp ?? null,
			}));

			setB2cProducts(processedData);
			setTotal(Number(res?.totalCount ?? 0));
		} catch (_e) {
			setB2cProducts([]);
			setTotal(0);
		} finally {
			setLoading(false);
		}
	};

	const openDescriptionModal = (product: any) => {
		const description = product?.upm_product_details?.description || "";
		setDescEdit(description);
		setDescProduct(product);
		setDescModalOpen(true);
	};

	const saveDescription = async () => {
		if (!descProduct) return;
		const mb_code = descProduct.mb_code;
		try {
			const res = await api.updateB2CDescription(mb_code, descEdit);
			if (res?.success) {
				toast({ title: "Description updated", description: res.message });
				setDescModalOpen(false);
				setDescProduct(null);
				getB2CProducts();
			} else {
				toast({
					title: "Update failed",
					description: res?.error || "Unable to update description",
					variant: "destructive",
				});
			}
		} catch (e: any) {
			toast({
				title: "Update failed",
				description: e?.message || "Unexpected error",
				variant: "destructive",
			});
		}
	};

	useEffect(() => {
		getB2CProducts();
		// Cleanup on page/limit change
		return () => {
			abortRef.current?.abort();
		};
	}, [page, limit, activeView]);

	// Debounce search to avoid spamming the API
	useEffect(() => {
		if (search.length === 0) {
			// When cleared, fetch default page fast
			getB2CProducts();
			return;
		}

		if (search.length < 2) return; // enforce min length

		const handle = setTimeout(() => {
			getB2CProducts();
		}, 300);
		return () => {
			clearTimeout(handle);
			abortRef.current?.abort();
		};
	}, [search, activeView]);

	const updateProduct = (id: string, data: any) => {
		api.updateB2CMasterProduct({ id, ...data }).then((res: any) => {
			if (res.success) {
				toast({
					title: "Product updated successfully",
					description: res.message,
				});
				getB2CProducts();
			} else {
				toast({
					title: "Error updating product",
					description: res.message,
				});
			}
		});
	};

	const toggleProductStatus = async (id: number, currentStatus: boolean) => {
		try {
			const res = await api.updateB2CProductStatus(id, !currentStatus);
			if (res?.success) {
				toast({
					title: "Status updated",
					description: res.message,
				});
				getB2CProducts();
			} else {
				toast({
					title: "Update failed",
					description: res?.error || "Unable to update status",
					variant: "destructive",
				});
			}
		} catch (e: any) {
			toast({
				title: "Update failed",
				description: e?.message || "Unexpected error",
				variant: "destructive",
			});
		}
	};

	const toggleDiscontinuedStatus = async (
		id: number,
		currentStatus: boolean,
	) => {
		try {
			const res = await api.updateB2CDiscontinuedStatus(id, !currentStatus);
			if (res?.success) {
				toast({
					title: "Discontinued updated",
					description: res.message,
				});
				getB2CProducts();
			} else {
				toast({
					title: "Update failed",
					description: res?.error || "Unable to update discontinued",
					variant: "destructive",
				});
			}
		} catch (e: any) {
			toast({
				title: "Update failed",
				description: e?.message || "Unexpected error",
				variant: "destructive",
			});
		}
	};

	return (
		<>
			<div className="container mx-auto p-4 bg-white rounded-md shadow-md dark:bg-gray-800">
				<div className="flex items-center justify-between mb-4">
					<h1 className="text-2xl font-bold text-black dark:text-white">
						B2C Products Master
					</h1>
				</div>
			  <div className="flex flex-row" style={{ justifyContent: "space-between" }}>
					<div className="w-1/4 my-4 relative">
						<Input
							placeholder="Search for a product"
							id="search"
							type="text"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<Button
							className={`absolute right-0 top-0 bg-none ${
								search.length > 0 ? "cursor-pointer" : "cursor-not-allowed"
							}`}
							onClick={() => {
								setSearch("");
								getB2CProducts();
							}}
							disabled={search.length === 0}
						>
							<X size={20} />
						</Button>
					</div>
					<div className="flex items-center gap-2">
					<Button
						variant={activeView === "rx" ? "default" : "outline"}
						className="px-4"
						onClick={() => {
							if (activeView === "rx") return;
							setActiveView("rx");
							setPage(1);
							setEditProductData({});
							setDescModalOpen(false);
							setDescProduct(null);
						}}
					>
						RX Products
					</Button>
					<Button
						variant={activeView === "upm" ? "default" : "outline"}
						className="px-4"
						onClick={() => {
							if (activeView === "upm") return;
							setActiveView("upm");
							setPage(1);
							setEditProductData({});
							setDescModalOpen(false);
							setDescProduct(null);
						}}
					>
						UPM Products
					</Button>
				</div>
				</div>
				<div className="w-full bg-white rounded-md shadow-md dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
					<Table>
						<TableHeader>
							<TableRow>
								{Object.entries(activeView === "rx" ? B2C_MASTER_PRODUCTS_COLUMNS : UPM_MASTER_PRODUCTS_COLUMNS).map(([key, value]) => (
									<TableHead key={key}>
										{value}
									</TableHead>
								))}
				{activeView === "rx" ? <TableHead>Actions</TableHead> : null}
							</TableRow>
						</TableHeader>
						<TableBody>
							{loading ? (
								<TableRow>
									<TableCell colSpan={10} className="text-center">
										<Loader />
									</TableCell>
								</TableRow>
							) : (
								b2cProducts.map((product: any, index: number) => (
									<TableRow key={product.id || product.mb_code || product.sku_id || `upm-${index}`}>
										{activeView === "rx" && editProductData.id === product.id ? (
											Object.entries(activeView === "rx" ? B2C_MASTER_PRODUCTS_COLUMNS : UPM_MASTER_PRODUCTS_COLUMNS).map(([column]) => (
												<TableCell key={column}>
													{column === "is_active" ? (
														<Switch
															checked={editProductData[column] || false}
															onCheckedChange={(checked) =>
																setEditProductData({
																	...editProductData,
																	[column]: checked,
																})
															}
														/>
													) : (
														<Input
															value={editProductData[column] || ""}
															onChange={(e) =>
																setEditProductData({
																	...editProductData,
																	[column]: e.target.value,
																})
															}
														/>
													)}
												</TableCell>
											))
										) : (
											Object.entries(activeView === "rx" ? B2C_MASTER_PRODUCTS_COLUMNS : UPM_MASTER_PRODUCTS_COLUMNS).map(([key, value]) => (
												<TableCell key={key}>
													{key === "mrp" ||
													key === "current_price" ||
													key === "discount" ? (
														product[key] ? (
															`₹${product[key]}`
														) : (
															"-"
														)
													) : key === "is_active" ? (
														activeView === "rx" ? (
															<Switch
																checked={product[key] || false}
																onCheckedChange={() =>
																	toggleProductStatus(
																		product.id,
																		product[key],
																	)
																}
															/>
														) : (
															<Switch checked={product[key] || false} disabled />
														)
													) : key === "discontinued" ? (
														activeView === "rx" ? (
															<Switch
																checked={product[key] || false}
																onCheckedChange={() =>
																	toggleDiscontinuedStatus(
																		product.id,
																		product[key],
																	)
																}
															/>
														) : (
															<Switch checked={product[key] || false} disabled />
														)
													) : (
														product[key] || "-"
													)}
												</TableCell>
											))
										)}
										{activeView === "rx" ? (
											<TableCell>
												{editProductData.id === product.id ? (
													<div className="flex gap-2">
														<Button
															onClick={() => {
																updateProduct(product.id, editProductData);
																setEditProductData({});
															}}
															className="bg-green-500 text-white"
														>
															<Check size={20} />
														</Button>
														<Button
															onClick={() => {
																setEditProductData({});
															}}
															className="bg-red-500 text-white"
														>
															<X size={20} />
														</Button>
														<Button
															onClick={() => openDescriptionModal(product)}
															className="text-white dark:bg-gray-700"
															title="Edit Description"
														>
															<FileText size={20} />
														</Button>
													</div>
												) : (
													<div className="flex gap-2">
														<Button
															onClick={() => {
																// setEditProductData(product);
															}}
															className="text-white dark:bg-gray-700"
															title="Edit"
														>
															<Pencil size={20} />
														</Button>
														<Button
															onClick={() => openDescriptionModal(product)}
															className="text-white dark:bg-gray-700"
															title="Edit Description"
														>
															<FileText size={20} />
														</Button>
													</div>
												)}
											</TableCell>
										) : null}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</div>
				<div className="flex justify-between items-center mt-4">
					<div className="w-1/6">
						<Select
							value={limit.toString()}
							onValueChange={(value) => setLimit(Number(value))}
						>
							<SelectTrigger>
								<SelectValue placeholder="Select limit" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="10">10</SelectItem>
								<SelectItem value="20">20</SelectItem>
								<SelectItem value="50">50</SelectItem>
							</SelectContent>
						</Select>
						<span className="text-sm text-gray-500 mt-2">items per page</span>
					</div>
					<Pagination
						page={page}
						totalPages={Math.ceil(total / limit)}
						onPageChange={setPage}
					/>
				</div>
			</div>

			<Dialog open={descModalOpen} onOpenChange={setDescModalOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Product Description</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-2">
						<Label htmlFor="description">Description</Label>
						<textarea
							id="description"
							value={descEdit}
							onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
								setDescEdit(e.target.value)
							}
							rows={8}
							className="block w-full rounded-md border border-gray-300 bg-transparent px-3 py-2 text-sm outline-none ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
						/>
					</div>
					<DialogFooter>
						<Button onClick={saveDescription}>Save</Button>
						<DialogClose asChild>
							<Button variant="outline">Cancel</Button>
						</DialogClose>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
